import { Change } from './layerTypes'

/**
 * Says in words what the comparison found.
 *
 * The comparison itself reports facts - `position.x` went from 120 to 132 -
 * and nothing else. Turning those into "moved +12x" is this file's whole job,
 * and it lives here rather than in the renderer because it is product copy: it
 * changes when someone decides the list reads badly, which is far more often
 * than the comparison changes, and a renderer release should not be what
 * stands between that decision and the page.
 *
 * Related properties are gathered back together here. They are reported apart
 * because a caller that wants only the horizontal movement should not have to
 * take a sentence apart to find it - but a reader wants "moved +12x +4y", not
 * two lines saying half of it each.
 */

const round = (value: number) => Math.round(value * 10) / 10

const quote = (value: string) => `“${value}”`

const asNumber = (value?: Change['before']) =>
  typeof value === 'number' ? value : 0

const asText = (value?: Change['before']) =>
  typeof value === 'string' ? value : ''

/** The change for one property path, or undefined when it did not change. */
const find = (changes: readonly Change[], property: string) =>
  changes.find(change => change.property === property)

/** Whether anything under a path changed - `style.fills` covers `.0.color`. */
const touched = (changes: readonly Change[], prefix: string) =>
  changes.some(
    change =>
      change.property === prefix || change.property.startsWith(`${prefix}.`)
  )

function describeMove(changes: readonly Change[]) {
  const x = find(changes, 'position.x')
  const y = find(changes, 'position.y')

  if (!x && !y) return null

  const dx = x ? round(asNumber(x.after) - asNumber(x.before)) : 0
  const dy = y ? round(asNumber(y.after) - asNumber(y.before)) : 0

  const horizontal = dx === 0 ? '' : `${dx > 0 ? '+' : ''}${dx}x`
  const vertical = dy === 0 ? '' : `${dy > 0 ? '+' : ''}${dy}y`
  const gap = horizontal && vertical ? ' ' : ''

  return `moved ${horizontal}${gap}${vertical}`
}

function describeResize(changes: readonly Change[]) {
  const width = find(changes, 'size.width')
  const height = find(changes, 'size.height')

  if (!width && !height) return null

  // The side that did not change still has to be named, or the reader is shown
  // half a size and left to guess the rest.
  const from = `${round(asNumber(width?.before))}×${round(
    asNumber(height?.before)
  )}`
  const to = `${round(asNumber(width?.after))}×${round(asNumber(height?.after))}`

  if (!width) return `resized to ${round(asNumber(height?.after))} tall`
  if (!height) return `resized to ${round(asNumber(width.after))} wide`

  return `resized from ${from} to ${to}`
}

function describeShape(change: Change) {
  if (!asText(change.before)) return 'shape added'
  if (!asText(change.after)) return 'shape removed'
  return 'shape changed'
}

function describeImages(change: Change) {
  const before = asText(change.before).split(', ').filter(Boolean)
  const after = asText(change.after).split(', ').filter(Boolean)

  if (before.length === 0) return 'image added'
  if (after.length === 0) return 'image removed'

  return before.length === 1 && after.length === 1
    ? 'image replaced'
    : 'images changed'
}

/**
 * Everything one changed layer or entity has to say, in reading order.
 *
 * Ordered by what a reader looks for first - what it is and whether it is
 * still there, then where and how big, then how it is painted - rather than by
 * the order the comparison happened to walk its properties in.
 */
export function describeChanges(changes: readonly Change[]): string {
  const said: string[] = []

  const name = find(changes, 'name')
  if (name) said.push(`renamed to ${quote(asText(name.after))}`)

  const hidden = find(changes, 'hidden')
  if (hidden) said.push(hidden.after === true ? 'hidden' : 'shown')

  const locked = find(changes, 'locked')
  if (locked) said.push(locked.after === true ? 'locked' : 'unlocked')

  const text = find(changes, 'text')
  if (text) {
    said.push(
      `text changed from ${quote(asText(text.before))} to ${quote(
        asText(text.after)
      )}`
    )
  }

  const moved = describeMove(changes)
  if (moved) said.push(moved)

  const resized = describeResize(changes)
  if (resized) said.push(resized)

  const rotation = find(changes, 'rotation')
  if (rotation) {
    said.push(
      `rotated from ${round(asNumber(rotation.before))}° to ${round(
        asNumber(rotation.after)
      )}°`
    )
  }

  const shape = find(changes, 'shape')
  if (shape) said.push(describeShape(shape))

  const images = find(changes, 'images')
  if (images) said.push(describeImages(images))

  if (touched(changes, 'corners')) said.push('corners changed')

  // Painted properties, each as one phrase however many of its own properties
  // differ: a reader told "fill changed" knows where to look, and the canvas
  // beside the list shows what it became.
  if (touched(changes, 'style.fills')) said.push('fill changed')
  if (touched(changes, 'style.borders')) said.push('border changed')
  if (touched(changes, 'style.shadows')) said.push('shadow changed')
  if (touched(changes, 'style.innerShadows')) said.push('inner shadow changed')
  if (touched(changes, 'style.tints')) said.push('tint changed')
  if (touched(changes, 'style.text')) said.push('text style changed')

  const blurEnabled = find(changes, 'style.blur.enabled')
  if (blurEnabled) {
    said.push(blurEnabled.after === true ? 'blur added' : 'blur removed')
  } else if (touched(changes, 'style.blur')) {
    said.push('blur changed')
  }

  const opacity = find(changes, 'style.opacity')
  if (opacity) {
    said.push(
      `opacity ${Math.round(asNumber(opacity.before) * 100)}% → ${Math.round(
        asNumber(opacity.after) * 100
      )}%`
    )
  }

  if (find(changes, 'style.blendMode')) said.push('blend mode changed')

  return said.join(', ')
}
